import { BROWSER_INDUCED_ABORT_NAME } from '@/shared/utils/browserLifecycle';
import { CHUNK_LOAD_ERROR_PATTERNS } from '@/shared/utils/chunkErrorHandler';
import type { ErrorEvent, EventHint } from '@sentry/react';
import {
  browserProfilingIntegration,
  browserSessionIntegration,
  browserTracingIntegration,
  captureConsoleIntegration,
  extraErrorDataIntegration,
  getReplay,
  httpClientIntegration,
  init,
  replayCanvasIntegration,
  replayIntegration,
  zodErrorsIntegration,
} from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_ENVIRONMENT ?? 'development';
const version = import.meta.env.VITE_VERSION;
export const SENTRY_REPLAY_KEY = 'enableSentryReplay';

/**
 * A few notes:
 *
 * Replays w/canvas only seem to work if you do it on initialization (i.e.
 * you can't lazy load the integration via `.addIntegration()`).
 *
 * https://github.com/getsentry/sentry-javascript/issues/13754
 *
 * Because whether we do replays is dependent on a team preference, we store that
 * info in localStorage and then stop the recording if we find out the user's
 * preference
 *
 * So the logic goes like this:
 *
 * - When the app loads the very first time, see if there's a preference in localStorage.
 * - If there's not, start the recording. Then when we load team data and find out
 *   they have a preference to not record, we'll stop it.
 * - If there is a preference, we'll use that. And if we find out later they changed it,
 *   we'll update localstorage and stop the recording.
 *
 * The two main paths into the app (dashboard and the file) are the two routes
 * where we'll run this logic.
 */

const NETWORK_ERROR_PATTERNS = [
  /Failed to fetch/,
  /Load failed/,
  /NetworkError/,
  /Failed to load dynamically imported/,
];

function isNetworkError(hint: EventHint): boolean {
  const error = hint.originalException;
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

// Worker shells reject their init promise with this error name when the
// underlying onerror was attributed to a browser-induced teardown (Memory
// Saver / tab discard / freeze / offline). If that rejection bubbles up to
// React or an unhandled-rejection breadcrumb, drop the Sentry event here so
// it doesn't slip past the `worker.onerror` suppression.
function isBrowserInducedAbort(hint: EventHint): boolean {
  const error = hint.originalException;
  return error instanceof Error && error.name === BROWSER_INDUCED_ABORT_NAME;
}

// httpClientIntegration auto-captures every non-2xx fetch as an unhandled
// error, regardless of whether the application caught it. For /version.json
// that's noise: Multiplayer.checkVersion polls it every few seconds while
// waiting for the CDN to serve the new bundle after a deploy, and its own
// catch/retry already handles transient errors. Drop only that URL; keep the
// integration active for every other path where 4xx/5xx really is actionable.
function isVersionJsonHttpClientCapture(event: ErrorEvent): boolean {
  const mechanismType = event.exception?.values?.[0]?.mechanism?.type;
  if (mechanismType !== 'auto.http.client.fetch') return false;
  const requestUrl = event.request?.url ?? '';
  return /\/version\.json(\?|#|$)/.test(requestUrl);
}

function isHandledPythonChartCaptureFallback(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== 'auto.core.capture_console') return false;
  const filename = exception.stacktrace?.frames?.find((frame) => frame.filename?.includes('pythonWebWorker'))?.filename;

  return (
    event.logger === 'console' &&
    filename !== undefined &&
    ['No content window', 'Plotly not available', 'No Plotly element found'].includes(exception.value ?? '')
  );
}

function isHandledEmojiMappingLoadFailure(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== 'auto.core.capture_console') return false;
  if (event.logger !== 'console') return false;

  const args = event.extra?.arguments;
  return Array.isArray(args) && args[0] === '[Emojis] Failed to load emoji mapping:';
}
function isExpiredWorkosAuthCodeCapture(event: ErrorEvent): boolean {
  const value = event.exception?.values?.[0]?.value ?? event.message ?? '';
  return /^The code '[^']+' has expired\.$/.test(value);
}

// captureConsoleIntegration forwards every `console.error(...)` call. When the
// call carries no actionable content (an empty/whitespace string, or no args at
// all) it lands in Sentry as a "<unlabeled event>" with an empty message, no
// stack, and no filename — impossible to action and pure noise. Drop those.
function isEmptyConsoleErrorCapture(event: ErrorEvent): boolean {
  if (event.logger !== 'console') return false;
  const args = event.extra?.arguments;
  if (!Array.isArray(args)) return false;
  return args.every((arg) => arg === undefined || arg === null || (typeof arg === 'string' && arg.trim() === ''));
}

// PixiJS logs WebGL initialization failures (shader compile/link, program, or
// context creation) via console.error, which captureConsoleIntegration turns
// into Sentry errors. These originate from Pixi's own stock shaders — e.g. the
// MSDF bitmap-font shader in this report — failing on the user's GPU. That's a
// client driver/hardware/context condition (old or blocklisted GPU, exhausted
// WebGL contexts, driver reset/TDR), not an app bug we can fix in code, so it's
// unactionable noise in the error feed.
const PIXI_WEBGL_INIT_FAILURE_PATTERNS = [
  /Could not initialize shader/,
  /Could not create shader program/,
  /Could not create a? ?WebGL context/,
];
function isPixiWebGLInitFailure(event: ErrorEvent): boolean {
  if (event.logger !== 'console') return false;
  const value = event.exception?.values?.[0]?.value ?? event.message ?? '';
  const args = event.extra?.arguments;
  const firstArg = Array.isArray(args) && typeof args[0] === 'string' ? args[0] : '';
  return PIXI_WEBGL_INIT_FAILURE_PATTERNS.some((pattern) => pattern.test(value) || pattern.test(firstArg));
}

function isZoteroConnectorExtensionError(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  const value = exception?.value ?? event.message ?? '';
  const frames = exception?.stacktrace?.frames ?? [];
  const hasWebkitMaskedFrame = frames.some((frame) => frame.filename?.startsWith('webkit-masked-url:'));

  return value.startsWith('Zotero Connector: Failed to send message') && hasWebkitMaskedFrame;
}

// The Google Search App in-app browser on iOS, together with Google Translate's
// DOM rewriting (the `translated-ltr` class it adds to <html>), injects
// sourceless scripts that recurse into the page and throw "RangeError: Maximum
// call stack size exceeded" via the global onerror handler. The stack never
// symbolicates — the only frame has filename "undefined" (a
// `js_no_source`/`missing_source` error) — so it can't point at our code. This
// is the same third-party category as the Translate-induced
// removeChild/insertBefore errors already dropped in `ignoreErrors`. Only drop
// the sourceless variant: a genuine recursion bug in our own bundle
// symbolicates to app frames and still reports.
function isSourcelessStackOverflow(event: ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.type !== 'RangeError') return false;
  if (!/Maximum call stack size exceeded/.test(exception.value ?? '')) return false;

  const frames = exception.stacktrace?.frames ?? [];
  const hasRealFrame = frames.some((frame) => {
    const file = frame.filename ?? frame.abs_path ?? '';
    return file !== '' && file !== 'undefined';
  });
  return !hasRealFrame;
}

export const initSentry = () => {
  let useReplay = true;
  if (localStorage.getItem(SENTRY_REPLAY_KEY) === 'false') {
    useReplay = false;
  }

  try {
    if (dsn && dsn !== 'none') {
      init({
        dsn,
        environment,
        release: `quadratic@${version}`,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: [
          /AuthKit: Failed to send logout request/,
          /Load failed \(authenticate\.quadratichq\.com\)/,
          /No user found after signin redirect/,
          /__wbindgen_malloc/,
          /InstantiationService has been disposed/,
          /Got bad scroll event/,
          /Data lost due to missing file/,
          /recursive use of an object detected/,
          /The request is not allowed by the user agent or the platform in the current context/,
          /Failed to execute 'removeChild' on 'Node'/,
          /Failed to execute 'insertBefore' on 'Node'/,
          /Couldn't exchange code/,
          /^Canceled$/,
          // Multiplayer.checkVersion's catch-block console.warn re-captured by
          // captureConsoleIntegration if levels ever expands to include 'warn'.
          /\[multiplayer\.ts\] checkVersion: Failed to fetch \/version\.json/,
          // Chunk loading errors are handled by chunkErrorHandler.ts which automatically reloads the page
          ...CHUNK_LOAD_ERROR_PATTERNS,
        ],
        denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-extension:\/\//],
        integrations: [
          browserProfilingIntegration(),
          browserSessionIntegration(),
          browserTracingIntegration(),
          captureConsoleIntegration({ levels: ['error'] }),
          extraErrorDataIntegration(),
          httpClientIntegration(),
          zodErrorsIntegration(),
          ...(useReplay
            ? [
                replayIntegration({
                  maskAllText: false,
                  maskAllInputs: false,
                  blockAllMedia: false,
                  networkDetailDenyUrls: ['/iframe-indexeddb'],
                  // Sentry classifies a click as "dead" (and 3+ repeats as a
                  // "rage click") when it produces no DOM mutation or page
                  // scroll within the timeout. Controls that respond only by
                  // scrolling an internal container — e.g. the sheet-bar
                  // navigation chevrons, which repeatedly nudge scrollLeft — get
                  // flagged as false-positive rage clicks even though clicking
                  // them repeatedly is normal. Opt those elements out with the
                  // `data-slow-click-ignore` marker.
                  slowClickIgnoreSelectors: ['[data-slow-click-ignore]'],
                }),
                replayCanvasIntegration({ enableManualSnapshot: false, quality: 'high' }),
              ]
            : []),
        ],
        beforeSend(event: ErrorEvent, hint: EventHint) {
          if (isNetworkError(hint)) {
            return null;
          }
          if (isBrowserInducedAbort(hint)) {
            return null;
          }
          if (isVersionJsonHttpClientCapture(event)) {
            return null;
          }
          if (isHandledPythonChartCaptureFallback(event)) {
            return null;
          }
          if (isHandledEmojiMappingLoadFailure(event)) {
            return null;
          }
          if (isExpiredWorkosAuthCodeCapture(event)) {
            return null;
          }
          if (isEmptyConsoleErrorCapture(event)) {
            return null;
          }
          if (isZoteroConnectorExtensionError(event)) {
            return null;
          }
          if (isPixiWebGLInitFailure(event)) {
            return null;
          }
          if (isSourcelessStackOverflow(event)) {
            return null;
          }
          return event;
        },
        profilesSampleRate: 1.0,
        tracesSampleRate: 1.0,
      });
    }
  } catch (error) {
    console.error('Error initializing Sentry', error);
  }
};

const stopRecording = () => {
  const replay = getReplay();
  if (replay) {
    replay.stop();
  }
};

export const handleSentryReplays = (useReplayPreferenceFromServer: boolean) => {
  // Get the current preference on the client
  const currentPreference = localStorage.getItem(SENTRY_REPLAY_KEY);

  // If it's never been set, we'll set it and stop the recording if it doesn't match the server preference
  if (currentPreference === null) {
    localStorage.setItem(SENTRY_REPLAY_KEY, useReplayPreferenceFromServer ? 'true' : 'false');
    if (useReplayPreferenceFromServer === false) {
      stopRecording();
    }
    return;
  }

  // If it has been set, we'll use that
  const useReplayFromClient = currentPreference === 'true';

  // If the preference is different from the server, we'll set it
  if (useReplayFromClient !== useReplayPreferenceFromServer) {
    localStorage.setItem(SENTRY_REPLAY_KEY, useReplayPreferenceFromServer ? 'true' : 'false');

    // And if they changed it to not record, we'll stop it
    if (useReplayPreferenceFromServer === false) {
      stopRecording();
    }
    return;
  }

  // Otherwise, do nothing
};
